/**
 * CutscenePanels — pure data for cutscene sequences.
 * No Phaser import. Rendered procedurally in IntroCutsceneScene / EndCutsceneScene.
 */

export interface CutscenePanel {
  /** Background fill colour (hex). */
  bgColor: number;
  /** Location / timestamp heading. */
  heading: string;
  /** Main body narration. */
  body: string;
  /** Accent colour for border and heading. */
  accentColor: number;
}

/** 3-panel Stage 1 intro sequence. */
export const INTRO_PANELS: readonly CutscenePanel[] = [
  {
    bgColor: 0x0b0e14,
    heading: 'CAMDEN MARKET — 23:47',
    body: 'A normal Wednesday in north London.\nNot for long.',
    accentColor: 0xff6b00,
  },
  {
    bgColor: 0x0a0c10,
    heading: 'OPERATION MANOR',
    body: '"Organised crime has seized control of\nthe old Victorian manor house\nat the end of the canal."\n\n— Metropolitan Police Bulletin',
    accentColor: 0xef233c,
  },
  {
    bgColor: 0x0d1018,
    heading: 'THE CALL',
    body: '"They need someone who knows\nhow to handle things the old way.\n\nThat means you."',
    accentColor: 0xffba08,
  },
];

/**
 * 4-panel Stage 1 end cutscene — Ghost reveal after Bargee KO.
 * Triggers ghost_unlocked in save state. Purely data; rendered by EndCutsceneScene.
 *
 * Also registered in CUTSCENES as 'stage1-end'.
 */
export const GHOST_REVEAL_PANELS: readonly CutscenePanel[] = [
  {
    bgColor: 0x0b0e14,
    heading: 'CANAL LOCK — STAGE 1 CLEAR',
    body: 'The Bargee collapses.\nHis runner breaks for the far towpath.',
    accentColor: 0xef233c,
  },
  {
    bgColor: 0x080a0f,
    heading: 'THE BRIDGE',
    body: 'A silhouette drops from the canal bridge.\nNo sound. No warning.',
    accentColor: 0x8d99ae,
  },
  {
    bgColor: 0x09090d,
    heading: 'RUNNER DOWN',
    body: 'One clean strike.\nThe runner folds before he reaches the road.\n\nWhoever that was, they knew exactly\nwhat they were doing.',
    accentColor: 0xffba08,
  },
  {
    bgColor: 0x0b0c12,
    heading: '"GHOST" JOINS THE CREW',
    body: '"Ex-22 SAS. I refused an order.\nThey buried me.\n\nLooks like I just dug myself out.\n\nCall me Ghost."',
    accentColor: 0x8d99ae,
  },
];

/** 3-panel Stage 2 intro — Soho neon descent, trafficking front on Berwick St. */
export const STAGE2_INTRO_PANELS: readonly CutscenePanel[] = [
  {
    bgColor: 0x0b0810,
    heading: 'SOHO — BERWICK STREET — 01:14',
    body: 'Neon bleeds through the rain.\nEvery doorway selling something.\nNot all of it on the menu.',
    accentColor: 0xff0066,
  },
  {
    bgColor: 0x0a070e,
    heading: 'THE FRONT',
    body: '"The club is legitimate.\nThe back office isn\'t.\n\nThey\'re moving people, not product.\nThat changes things."',
    accentColor: 0x00e5ff,
  },
  {
    bgColor: 0x0d0b14,
    heading: 'LAST KNOWN LOCATION',
    body: 'Three women.\nTaken off the books four days ago.\n\nSomewhere behind that door.',
    accentColor: 0xef233c,
  },
];

/** 3-panel Stage 3 intro — Bermondsey docks, Calais-crossing cartel cargo. */
export const STAGE3_INTRO_PANELS: readonly CutscenePanel[] = [
  {
    bgColor: 0x080c0f,
    heading: 'BERMONDSEY — CONTAINER DOCKS — 03:22',
    body: 'The Thames smells like diesel and rust.\nA container ship killed its lights\na mile out from the wharf.',
    accentColor: 0xffba08,
  },
  {
    bgColor: 0x070a0d,
    heading: 'THE CARGO',
    body: '"Same cartel that runs the Calais crossing.\nThey packed them in like freight.\n\nWe traced the manifest\nback to a dead shipping firm."',
    accentColor: 0x8ecae6,
  },
  {
    bgColor: 0x090c10,
    heading: 'DOCK BOSS',
    body: 'Someone on the inside\nchecks every container before customs.\n\nFind him.\nGet the manifest.',
    accentColor: 0xef233c,
  },
];

/** 3-panel Stage 4 intro — Northern Line, last tube courier chase. */
export const STAGE4_INTRO_PANELS: readonly CutscenePanel[] = [
  {
    bgColor: 0x080a0c,
    heading: 'NORTHERN LINE — STOCKWELL — 00:03',
    body: 'Last tube of the night.\nThree carriages.\nOne very nervous courier.',
    accentColor: 0x00e5ff,
  },
  {
    bgColor: 0x060809,
    heading: 'THE DROP',
    body: '"He picks up from Soho, delivers south.\nWe\'ve missed him twice.\n\nThis is the only route he runs.\nDon\'t let him reach the end of the line."',
    accentColor: 0xffba08,
  },
  {
    bgColor: 0x080b0e,
    heading: 'NEXT STOP',
    body: "Tube rattles into the dark.\nHe's clocked you.\n\nRun.",
    accentColor: 0xef233c,
  },
];

/** 3-panel Stage 5 intro — Croydon factory lab, spice supply chain. */
export const STAGE5_INTRO_PANELS: readonly CutscenePanel[] = [
  {
    bgColor: 0x0a0c08,
    heading: 'CROYDON — DISUSED FACTORY — 04:47',
    body: 'Industrial estate.\nEvery unit dark except one.\nThe air tastes of chemicals.',
    accentColor: 0x80ff00,
  },
  {
    bgColor: 0x090b07,
    heading: 'THE LAB',
    body: '"Synthetic spice.\nCheaper than heroin, harder to trace.\n\nThey\'ve been cooking here for six months.\nNo one noticed because no one looked."',
    accentColor: 0xffba08,
  },
  {
    bgColor: 0x0b0e09,
    heading: 'SHUT IT DOWN',
    body: "The chemist is still inside.\nSo is the ledger with every dealer\nthey've ever supplied.\n\nBoth are coming out.",
    accentColor: 0xef233c,
  },
];

/** 3-panel Stage 6 intro — Bethnal Green anti-corruption rally; Crimson Cadre move in. */
export const STAGE6_INTRO_PANELS: readonly CutscenePanel[] = [
  {
    bgColor: 0x0c0a0f,
    heading: 'BETHNAL GREEN — YORK HALL — 18:30',
    body: 'A thousand voices.\nBanners, placards, peaceable anger.\nThe anti-corruption rally has been weeks in the making.',
    accentColor: 0xcc0000,
  },
  {
    bgColor: 0x0b0910,
    heading: 'THE CADRE ARRIVES',
    body: '"Red scarves.\nSplit into three groups.\n\nThey didn\'t come to protest."',
    accentColor: 0xff4444,
  },
  {
    bgColor: 0x0d0b12,
    heading: 'KEEP IT PEACEFUL',
    body: "The crowd doesn't know what's coming.\nThe Crimson Cadre is there to make sure\nthis rally becomes a riot.\n\nStop them before the cameras roll.",
    accentColor: 0xef233c,
  },
];

/**
 * 4-panel Stage 6 end reveal — Astor-Pemberton handler pays Comrade Vex.
 * Proves the unrest is manufactured from the top.
 */
export const STAGE6_END_PANELS: readonly CutscenePanel[] = [
  {
    bgColor: 0x090810,
    heading: 'BETHNAL GREEN — STAGE 6 CLEAR',
    body: 'The rally ends without bloodshed.\nBut Comrade Vex slips away\nbefore you can reach him.',
    accentColor: 0xcc0000,
  },
  {
    bgColor: 0x07060e,
    heading: 'BACK STREET — 21:09',
    body: 'Vex doubles back through a service lane.\nA figure in a charcoal coat is waiting.\n\nNo introduction needed.',
    accentColor: 0x8d99ae,
  },
  {
    bgColor: 0x08070f,
    heading: 'THE PAYMENT',
    body: 'A transfer on a burner phone.\nFive figures.\n\n"As agreed. Next time finish the job."\n\nThe voice is clipped. Private school.\nSomeone used to giving orders.',
    accentColor: 0xffba08,
  },
  {
    bgColor: 0x0a0912,
    heading: 'ASTOR-PEMBERTON',
    body: "The handler turns into the light\nfor just a second.\n\nYou know that face from the financial pages.\n\nThe unrest wasn't grassroots.\nIt was bought and paid for from the top.",
    accentColor: 0xef233c,
  },
];

/** 3-panel Stage 7 intro — Dover storm night, intercept the landing boats. */
export const STAGE7_INTRO_PANELS: readonly CutscenePanel[] = [
  {
    bgColor: 0x080c12,
    heading: 'DOVER — WHITE CLIFFS — 02:15',
    body: 'Force 7 off the Channel.\nRain horizontal.\nSomewhere below, engines in the dark.',
    accentColor: 0x4488cc,
  },
  {
    bgColor: 0x070a10,
    heading: 'THE LANDING',
    body: '"Three rigid inflatables.\nCargo we don\'t want on British soil.\n\nThey\'ve done this route four times.\nTonight it stops."',
    accentColor: 0x8ecae6,
  },
  {
    bgColor: 0x090d14,
    heading: 'THE BEACH',
    body: 'One road in.\nOne road out.\n\nGet down there\nbefore they reach the cliff path.',
    accentColor: 0xef233c,
  },
];

/** 3-panel Stage 8 intro — Canary Wharf, money trail leads to Astor-Pemberton's tower. */
export const STAGE8_INTRO_PANELS: readonly CutscenePanel[] = [
  {
    bgColor: 0x080a10,
    heading: 'CANARY WHARF — DAWN',
    body: 'Glass and steel.\nThe money laundered through\nthree shell companies ends here.',
    accentColor: 0x00b4d8,
  },
  {
    bgColor: 0x070910,
    heading: 'THE TOWER',
    body: '"Astor-Pemberton Capital Management.\nForty-second floor.\n\nEvery dirty transaction\nfrom Camden to Dover\nclearance through that office."',
    accentColor: 0xffba08,
  },
  {
    bgColor: 0x090b12,
    heading: 'THE EVIDENCE',
    body: "The ledger is up there.\nSo are the people who think\nthey're untouchable.\n\nProve them wrong.",
    accentColor: 0xef233c,
  },
];

/** 3-panel Stage 9 intro — Westminster, march on Parliament, Halloway cornered. */
export const STAGE9_INTRO_PANELS: readonly CutscenePanel[] = [
  {
    bgColor: 0x090a0e,
    heading: 'WESTMINSTER — PARLIAMENT SQUARE — 11:00',
    body: 'Ten thousand people on the streets.\nA city demanding answers.\n\nInside, Lord Halloway prepares to resign\nbefore the questions begin.',
    accentColor: 0xc8aa6e,
  },
  {
    bgColor: 0x08090d,
    heading: 'LAST CHANCE',
    body: '"He\'s moving through the Commons tunnel.\nPrivate security.\nSix men.\n\nIf he reaches the car, he disappears\ninto a private flight to Geneva."',
    accentColor: 0xffba08,
  },
  {
    bgColor: 0x0a0b10,
    heading: 'CORNERED',
    body: "The crowd outside doesn't know\nhow close the truth is.\n\nHalloway does.\n\nHe's running.",
    accentColor: 0xef233c,
  },
];

/** 2-panel Halloway offer — shown entering DealScene (Stage 8 clear → bridge). */
export const HALLOWAY_DEAL_PANELS: readonly CutscenePanel[] = [
  {
    bgColor: 0x06080f,
    heading: 'WESTMINSTER BRIDGE — STAGE 8 CLEAR',
    body: "The ledger is in your hands.\nHalloway's network lies exposed.\n\nBut Halloway is still out there.\nAnd he's waiting for you on the bridge.",
    accentColor: 0xc8aa6e,
  },
  {
    bgColor: 0x080c14,
    heading: 'THE OFFER',
    body: '"You\'ve impressed me.\nI could use someone like you.\n\nWalk away. Name your price.\nOr keep fighting for people\nwho don\'t even know your name."\n\n"What do you say?"',
    accentColor: 0xffba08,
  },
];

/**
 * 4-panel Halloway betrayal — player accepts, Halloway lies, player is seized
 * and dumped back at Bethnal Green, setting up the Stage-6 replay loop.
 */
export const HALLOWAY_BETRAYAL_PANELS: readonly CutscenePanel[] = [
  {
    bgColor: 0x06080c,
    heading: 'WESTMINSTER BRIDGE — DEAL MADE',
    body: '"Wise choice."\n\nHalloway smiles.\nHis security move closer.',
    accentColor: 0xffba08,
  },
  {
    bgColor: 0x050608,
    heading: 'THE LIE',
    body: '"You actually believed him?"\n\nOne phone call.\nThree words.\n"Take them in."',
    accentColor: 0xef233c,
  },
  {
    bgColor: 0x040507,
    heading: 'SEIZED',
    body: 'Zip-ties.\nA hood.\nThen nothing.',
    accentColor: 0x8d99ae,
  },
  {
    bgColor: 0x060810,
    heading: 'DUMPED AT THE RIVER — BETHNAL GREEN — NIGHT',
    body: 'You wake on wet concrete.\nSomewhere you\'ve been before.\n\nStage 6. Back at the beginning.\n\n"He always burns his assets.\nAlways."',
    accentColor: 0xcc0000,
  },
];

/** BAD ENDING — deal taken twice, the player became one of them. */
export const ENDING_SELLOUT_PANELS: readonly CutscenePanel[] = [
  {
    bgColor: 0x050708,
    heading: 'ONE YEAR LATER',
    body: 'The manor house gleams.\nPainted shutters.\nA new corporate nameplate.',
    accentColor: 0xffba08,
  },
  {
    bgColor: 0x040506,
    heading: 'NEW MANAGEMENT',
    body: '"Valued partner."\n\nThat\'s what they call you now.\nThe streets remember a different name.',
    accentColor: 0x8d99ae,
  },
  {
    bgColor: 0x050608,
    heading: 'THE PRICE',
    body: 'The trafficking routes still run.\nThe Bent Coppers still collect.\nThe estate still starves.\n\nBut your account is full.',
    accentColor: 0xef233c,
  },
  {
    bgColor: 0x030405,
    heading: 'ENDING — SELL-OUT',
    body: "You didn't just lose the fight.\n\nYou became the reason\nsomeone else will have to fight it.",
    accentColor: 0xcc0000,
  },
];

/**
 * TRUE ENDING — REFUSE path payoff after Halloway + Astor fall.
 * Also registered as 'stage9-end' so nextRoute(9, …) auto-triggers it.
 */
export const ENDING_TRUE_PANELS: readonly CutscenePanel[] = [
  {
    bgColor: 0x06080e,
    heading: 'WESTMINSTER — STAGE 9 CLEAR',
    body: "Halloway on the floor.\nParliament's marble cold beneath him.\n\nThe cameras are already rolling.",
    accentColor: 0xc8aa6e,
  },
  {
    bgColor: 0x070910,
    heading: 'THE EXPOSURE',
    body: "The ledger goes public at midnight.\nThirty-seven names.\nFour MPs.\nTwo chief inspectors.\n\nAstor-Pemberton's accounts frozen by morning.",
    accentColor: 0xffba08,
  },
  {
    bgColor: 0x080a12,
    heading: 'AFTERMATH',
    body: 'Three weeks of headlines.\nA parliamentary inquiry.\nNine arrests.\n\nThe manor house seized under the\nProceeds of Crime Act.',
    accentColor: 0x06d6a0,
  },
  {
    bgColor: 0x090b14,
    heading: 'ENDING — TRUE',
    body: "Camden Market opens at six.\nThe canal still smells of diesel.\n\nBut the lock keeper's got both eyes open now.\n\nSo do the people.",
    accentColor: 0x4488cc,
  },
];

// ── PM fate epilogue cutscenes (MY-1979) — one per ending ────────────────────

export const ENDING_PEOPLES_VERDICT_PANELS: readonly CutscenePanel[] = [
  {
    bgColor: 0x080c12,
    heading: 'PARLIAMENT — THREE MONTHS LATER',
    body: 'Thirty-seven names in the ledger.\nThirty-seven cases opened.\n\nHalloway testifies on day one\nof the longest public inquiry in a decade.',
    accentColor: 0x06d6a0,
  },
  {
    bgColor: 0x090d14,
    heading: "ENDING — THE PEOPLE'S VERDICT",
    body: 'Not every conviction sticks.\nNot every name pays.\n\nBut for the first time in years,\nthe courts are trying.\n\nSo are the people.',
    accentColor: 0x4488cc,
  },
];

export const ENDING_WHOLE_TRUTH_PANELS: readonly CutscenePanel[] = [
  {
    bgColor: 0x060810,
    heading: 'MIDNIGHT — FOUR MAJOR NEWSROOMS',
    body: 'The MANOR files hit every server simultaneously.\nThirty-seven names.\nTwo hundred pages of evidence.\n\nBy 2 a.m. no one is sleeping.',
    accentColor: 0xffba08,
  },
  {
    bgColor: 0x08090e,
    heading: 'ENDING — THE WHOLE TRUTH',
    body: "You can't bury a story this size.\n\nNot when it's already in everyone's hands.",
    accentColor: 0xffffff,
  },
];

export const ENDING_BIGGER_THAN_HIM_PANELS: readonly CutscenePanel[] = [
  {
    bgColor: 0x09090d,
    heading: 'PARLIAMENT — 11:23',
    body: "You step over him without a word.\n\nHe calls something after you.\nYou don't look back.",
    accentColor: 0x8d99ae,
  },
  {
    bgColor: 0x080c12,
    heading: 'ENDING — BIGGER THAN HIM',
    body: 'The fight was never about him.\n\nHe knows that now.\nSo do you.',
    accentColor: 0x06d6a0,
  },
];

export const ENDING_MEET_THE_NEW_BOSS_PANELS: readonly CutscenePanel[] = [
  {
    bgColor: 0x050708,
    heading: 'THREE MONTHS LATER',
    body: 'Different nameplate.\nSame office.\n\n"Valued partner."\n\nThat\'s what they call you now.',
    accentColor: 0xffba08,
  },
  {
    bgColor: 0x040506,
    heading: 'ENDING — MEET THE NEW BOSS',
    body: 'The routes still run.\nThe system still feeds.\n\nYou sit at the top of it.\n\nSame as it ever was.',
    accentColor: 0xef233c,
  },
];

export const ENDING_CANCELLED_PANELS: readonly CutscenePanel[] = [
  {
    bgColor: 0x0a080f,
    heading: 'LIVE — 342,000 VIEWERS',
    body: 'The stream goes up before anyone can kill it.\n\nHalloway grovels for eleven minutes.\nSomebody clips it.\n\nIt becomes a meme by afternoon.',
    accentColor: 0x00e5ff,
  },
  {
    bgColor: 0x08060c,
    heading: 'ENDING — CANCELLED',
    body: "He can't resign.\nHe can't stay.\n\nHe can't go outside without someone pointing.\n\nThe internet has a longer memory than the courts.",
    accentColor: 0xff00cc,
  },
];

export const ENDING_STRINGS_ATTACHED_PANELS: readonly CutscenePanel[] = [
  {
    bgColor: 0x080a10,
    heading: 'TUESDAY BRIEFINGS — EVERY WEEK',
    body: "He arrives punctually now.\nHe listens.\n\nHe doesn't speak unless spoken to.",
    accentColor: 0x8d99ae,
  },
  {
    bgColor: 0x070910,
    heading: 'ENDING — STRINGS ATTACHED',
    body: 'The puppet sits.\nThe puppeteer stands.\n\nPower is just leverage\nunder a different name.',
    accentColor: 0xaaaaaa,
  },
];

export const ENDING_LIVE_AT_TEN_PANELS: readonly CutscenePanel[] = [
  {
    bgColor: 0x080b10,
    heading: 'ITV STUDIO — 22:03',
    body: "He reads from the paper you drafted.\nEvery word.\n\nThe autocue doesn't shake.\nHis hands do.",
    accentColor: 0xef233c,
  },
  {
    bgColor: 0x060810,
    heading: 'ENDING — LIVE AT TEN',
    body: 'Seven MPs announce resignations by midnight.\n\nThe PM\'s office issues a statement at 06:00.\n\n"Personal reasons."',
    accentColor: 0xffba08,
  },
];

export const ENDING_OUT_THE_FRONT_DOOR_PANELS: readonly CutscenePanel[] = [
  {
    bgColor: 0x060608,
    heading: 'PARLIAMENT — 11:31',
    body: 'You throw open the lobby doors.\nThe crowd outside goes quiet for one second.\n\nThen they see him.',
    accentColor: 0xcc0000,
  },
  {
    bgColor: 0x040404,
    heading: 'ENDING — OUT THE FRONT DOOR',
    body: '— fade to black —\n\nThe evening news leads with\n"disturbance at Parliament".\n\nNo charges filed.',
    accentColor: 0x444444,
  },
];

export const ENDING_SALT_THE_EARTH_PANELS: readonly CutscenePanel[] = [
  {
    bgColor: 0x060808,
    heading: 'THE ARCHIVE — 11:40',
    body: 'You walk past Halloway.\nYou walk to the filing room.\n\nThe MANOR records have been here for sixty years.\n\nNot any more.',
    accentColor: 0x804000,
  },
  {
    bgColor: 0x050606,
    heading: 'ENDING — SALT THE EARTH',
    body: "Nothing left to prosecute.\nNothing left to fear.\nNothing left to build on.\n\nSometimes that's the point.",
    accentColor: 0x888888,
  },
];

export const ENDING_ONE_LAST_USE_PANELS: readonly CutscenePanel[] = [
  {
    bgColor: 0x080c10,
    heading: 'EMERGENCY SESSION — HOUSE OF COMMONS',
    body: 'He reads the repeal bill aloud.\nHand shaking.\nVoice flat.\n\nSix laws struck down in one session.',
    accentColor: 0x06d6a0,
  },
  {
    bgColor: 0x090e12,
    heading: 'ENDING — ONE LAST USE',
    body: "He's useless to you now.\n\nYou leave him at the dispatch box\nand let someone else decide\nwhat comes next.",
    accentColor: 0xffba08,
  },
];

export const ENDING_EMPTY_CHAIR_PANELS: readonly CutscenePanel[] = [
  {
    bgColor: 0x090a0c,
    heading: "THE PM'S OFFICE — 11:44",
    body: 'You sit in the chair for twelve seconds.\n\nOne photograph.\nNo caption.',
    accentColor: 0xc8aa6e,
  },
  {
    bgColor: 0x0a0b0e,
    heading: 'ENDING — THE EMPTY CHAIR',
    body: 'It goes up anonymously.\n\nEveryone knows what it means.\n\nNo one can prove a thing.',
    accentColor: 0x8d99ae,
  },
];

export const ENDING_GHOST_PROTOCOL_PANELS: readonly CutscenePanel[] = [
  {
    bgColor: 0x06080c,
    heading: 'WESTMINSTER — 11:29',
    body: "You're gone before anyone notices\nHalloway is alone.\n\nThe evidence goes with you.\n\nNo handover. No press conference.",
    accentColor: 0x8d99ae,
  },
  {
    bgColor: 0x050609,
    heading: 'ENDING — GHOST PROTOCOL',
    body: 'Somewhere, the work continues.\n\nUnder a different name.\nIn a different city.\n\nYou were never really here.',
    accentColor: 0x4488cc,
  },
];

export const ENDING_FULL_CIRCLE_PANELS: readonly CutscenePanel[] = [
  {
    bgColor: 0x07090e,
    heading: 'THREE MONTHS LATER — WESTMINSTER',
    body: 'It took longer than expected.\nIt always does.\n\nBut the inquiry is real.\nThe convictions are real.\nThe law is changing.',
    accentColor: 0x06d6a0,
  },
  {
    bgColor: 0x080a12,
    heading: 'ENDING — FULL CIRCLE',
    body: 'Not a revolution.\nA shift.\n\nThe first honest thing in years.\n\nCamden Market opens at six.\nEveryone notices.',
    accentColor: 0x4488cc,
  },
];

export const ENDING_FOLLOW_THE_MONEY_PANELS: readonly CutscenePanel[] = [
  {
    bgColor: 0x070810,
    heading: 'FIFTEEN MINUTES LATER — WESTMINSTER',
    body: 'Astor-Pemberton steps forward\nbefore the press arrive.\n\n"The PM was always a proxy.\nThe people you want\nleft the country two hours ago."',
    accentColor: 0xffba08,
  },
  {
    bgColor: 0x06070e,
    heading: 'ENDING — FOLLOW THE MONEY',
    body: 'The money is already offshore.\nThe names are already changed.\n\nHalloway was the front.\n\nSomewhere, the real network\nis watching the news\nand laughing.\n\n— TO BE CONTINUED —',
    accentColor: 0xef233c,
  },
];

/**
 * Central cutscene registry.
 * Key: stable string id used in scene.start() init data.
 * Value: the panel array to render.
 */
export const CUTSCENES: Record<string, readonly CutscenePanel[]> = {
  'stage1-intro': INTRO_PANELS,
  'stage1-end': GHOST_REVEAL_PANELS,
  'stage2-intro': STAGE2_INTRO_PANELS,
  'stage3-intro': STAGE3_INTRO_PANELS,
  'stage4-intro': STAGE4_INTRO_PANELS,
  'stage5-intro': STAGE5_INTRO_PANELS,
  'stage6-intro': STAGE6_INTRO_PANELS,
  'stage6-end': STAGE6_END_PANELS,
  'stage7-intro': STAGE7_INTRO_PANELS,
  'stage8-intro': STAGE8_INTRO_PANELS,
  'stage9-intro': STAGE9_INTRO_PANELS,
  'halloway-deal': HALLOWAY_DEAL_PANELS,
  'halloway-betrayal': HALLOWAY_BETRAYAL_PANELS,
  'ending-sellout': ENDING_SELLOUT_PANELS,
  'ending-true': ENDING_TRUE_PANELS,
  // Alias: stage9-end auto-triggers the true ending via nextRoute(9, …)
  'stage9-end': ENDING_TRUE_PANELS,
  // ── PM fate endings (MY-1979) ──────────────────────────────────────────────
  'ending-peoples-verdict': ENDING_PEOPLES_VERDICT_PANELS,
  'ending-whole-truth': ENDING_WHOLE_TRUTH_PANELS,
  'ending-bigger-than-him': ENDING_BIGGER_THAN_HIM_PANELS,
  'ending-meet-the-new-boss': ENDING_MEET_THE_NEW_BOSS_PANELS,
  'ending-cancelled': ENDING_CANCELLED_PANELS,
  'ending-strings-attached': ENDING_STRINGS_ATTACHED_PANELS,
  'ending-live-at-ten': ENDING_LIVE_AT_TEN_PANELS,
  'ending-out-the-front-door': ENDING_OUT_THE_FRONT_DOOR_PANELS,
  'ending-salt-the-earth': ENDING_SALT_THE_EARTH_PANELS,
  'ending-one-last-use': ENDING_ONE_LAST_USE_PANELS,
  'ending-the-empty-chair': ENDING_EMPTY_CHAIR_PANELS,
  'ending-ghost-protocol': ENDING_GHOST_PROTOCOL_PANELS,
  'ending-full-circle': ENDING_FULL_CIRCLE_PANELS,
  'ending-follow-the-money': ENDING_FOLLOW_THE_MONEY_PANELS,
};

/**
 * Resolve a cutscene by id. Throws for unknown ids so callers fail fast.
 */
export function getCutscene(id: string): readonly CutscenePanel[] {
  const panels = CUTSCENES[id];
  if (!panels) throw new Error(`Unknown cutscene id: "${id}"`);
  return panels;
}

/** Returns true if a cutscene with the given id is registered. */
export function hasCutscene(id: string): boolean {
  return id in CUTSCENES;
}
